// T10 — useClaimMedia: lists/uploads/deletes/replaces claim photos & videos
// via api-client.ts + React Query, replacing the legacy media tab's
// raw `fetch()` + `localStorage`-read auth-header pattern.

import { useMutation, useQuery, useQueryClient, type UseMutateAsyncFunction } from '@tanstack/react-query';
import {
  deleteClaimPhoto,
  deleteClaimVideo,
  listClaimPhotos,
  listClaimVideos,
  uploadClaimPhoto,
  uploadClaimVideo,
} from '@/lib/vehicle-claims/api';
import type { MediaRecord } from '../types';
import type { RequestContext } from '../requestContext';

export const claimMediaKeys = {
  photos: (jobId: string) => ['claim-media', jobId, 'photos'] as const,
  videos: (jobId: string) => ['claim-media', jobId, 'videos'] as const,
};

// Stable reference so `photos ?? EMPTY_MEDIA` / `videos ?? EMPTY_MEDIA` doesn't
// hand downstream useMemo/useEffect deps a brand-new [] identity every render
// while the query is loading, disabled, or errored.
const EMPTY_MEDIA: MediaRecord[] = [];

export interface UploadPhotoInput {
  file: File;
  photoType: string;
}

export interface UploadVideoInput {
  file: File;
  videoType: string;
}

export interface ReplacePhotoInput extends UploadPhotoInput {
  existingId: string;
}

export interface ReplaceVideoInput extends UploadVideoInput {
  existingId: string;
}

export interface UseClaimMediaResult {
  photos: MediaRecord[];
  videos: MediaRecord[];
  isLoadingPhotos: boolean;
  isLoadingVideos: boolean;
  photosError: Error | null;
  videosError: Error | null;
  uploadPhoto: UseMutateAsyncFunction<MediaRecord, Error, UploadPhotoInput>;
  uploadVideo: UseMutateAsyncFunction<MediaRecord, Error, UploadVideoInput>;
  deletePhoto: UseMutateAsyncFunction<void, Error, string>;
  deleteVideo: UseMutateAsyncFunction<void, Error, string>;
  replacePhoto: UseMutateAsyncFunction<MediaRecord, Error, ReplacePhotoInput>;
  replaceVideo: UseMutateAsyncFunction<MediaRecord, Error, ReplaceVideoInput>;
  isUploadingPhoto: boolean;
  isUploadingVideo: boolean;
  isDeletingPhoto: boolean;
  isDeletingVideo: boolean;
  isReplacingPhoto: boolean;
  isReplacingVideo: boolean;
}

export function useClaimMedia(jobId: string, context?: RequestContext): UseClaimMediaResult {
  const queryClient = useQueryClient();

  // Spread as a trailing arg only when present, so staff call sites (no
  // context) invoke the api.ts functions with the exact same arity as
  // before this feature — never an explicit `undefined` extra argument.
  const ctxArgs = context ? [context] as const : [];

  const photosQuery = useQuery({
    queryKey: claimMediaKeys.photos(jobId),
    queryFn: () => listClaimPhotos(jobId, ...ctxArgs),
    enabled: Boolean(jobId),
    // AI enrichment (damage detection / plate / VIN OCR) now runs via a
    // server-side background poller (cr067) instead of a browser-held
    // fetch, so the client picks up results by polling while any photo is
    // still queued/in-progress — no manual refresh needed. Stops polling
    // once every enrichable photo has settled to done/failed.
    refetchInterval: (query) => {
      const photos = query.state.data;
      if (!photos) return false;
      const hasPending = photos.some(
        (p) => p.enrichment_status === 'pending' || p.enrichment_status === 'processing',
      );
      return hasPending ? 5_000 : false;
    },
  });

  const videosQuery = useQuery({
    queryKey: claimMediaKeys.videos(jobId),
    queryFn: () => listClaimVideos(jobId, ...ctxArgs),
    enabled: Boolean(jobId),
  });

  function invalidateMedia() {
    queryClient.invalidateQueries({ queryKey: claimMediaKeys.photos(jobId) });
    queryClient.invalidateQueries({ queryKey: claimMediaKeys.videos(jobId) });
  }

  const uploadPhotoMutation = useMutation({
    mutationFn: ({ file, photoType }: UploadPhotoInput) => uploadClaimPhoto(jobId, file, photoType, ...ctxArgs),
    onSuccess: invalidateMedia,
  });

  const uploadVideoMutation = useMutation({
    mutationFn: ({ file, videoType }: UploadVideoInput) => uploadClaimVideo(jobId, file, videoType, ...ctxArgs),
    onSuccess: invalidateMedia,
  });

  const deletePhotoMutation = useMutation({
    mutationFn: (photoId: string) => deleteClaimPhoto(jobId, photoId, ...ctxArgs),
    onSuccess: invalidateMedia,
  });

  const deleteVideoMutation = useMutation({
    mutationFn: (videoId: string) => deleteClaimVideo(jobId, videoId, ...ctxArgs),
    onSuccess: invalidateMedia,
  });

  // "Replace" has no dedicated backend endpoint — it uploads the new file
  // FIRST, then deletes the old record, matching the two calls the legacy
  // UploadSlot's Replace button effectively required. Upload-first (not
  // delete-first) so a failed upload — network drop, rejected file, timeout —
  // never leaves the slot empty with nothing to show for it; the old photo/
  // video stays in place until the replacement is confirmed saved.
  const replacePhotoMutation = useMutation({
    mutationFn: async ({ existingId, file, photoType }: ReplacePhotoInput) => {
      const uploaded = await uploadClaimPhoto(jobId, file, photoType, ...ctxArgs);
      await deleteClaimPhoto(jobId, existingId, ...ctxArgs);
      return uploaded;
    },
    onSuccess: invalidateMedia,
  });

  const replaceVideoMutation = useMutation({
    mutationFn: async ({ existingId, file, videoType }: ReplaceVideoInput) => {
      const uploaded = await uploadClaimVideo(jobId, file, videoType, ...ctxArgs);
      await deleteClaimVideo(jobId, existingId, ...ctxArgs);
      return uploaded;
    },
    onSuccess: invalidateMedia,
  });

  return {
    photos: photosQuery.data ?? EMPTY_MEDIA,
    videos: videosQuery.data ?? EMPTY_MEDIA,
    isLoadingPhotos: photosQuery.isLoading,
    isLoadingVideos: videosQuery.isLoading,
    photosError: photosQuery.error,
    videosError: videosQuery.error,
    uploadPhoto: uploadPhotoMutation.mutateAsync,
    uploadVideo: uploadVideoMutation.mutateAsync,
    deletePhoto: deletePhotoMutation.mutateAsync,
    deleteVideo: deleteVideoMutation.mutateAsync,
    replacePhoto: replacePhotoMutation.mutateAsync,
    replaceVideo: replaceVideoMutation.mutateAsync,
    isUploadingPhoto: uploadPhotoMutation.isPending,
    isUploadingVideo: uploadVideoMutation.isPending,
    isDeletingPhoto: deletePhotoMutation.isPending,
    isDeletingVideo: deleteVideoMutation.isPending,
    isReplacingPhoto: replacePhotoMutation.isPending,
    isReplacingVideo: replaceVideoMutation.isPending,
  };
}
